import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api, authFetch, API_BASE, throwIfNotOk } from '../../lib/api';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import { formatDate } from '../../lib/format';
import { useAuth } from '../../lib/authContext';
import { qk } from '../../lib/queryKeys';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';

export const Route = createFileRoute('/transactions/$transactionId')({
  component: TransactionDetailPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransactionDetail {
  id: string;
  closedAt: string;
  assetId: string | null;
  skuId: string | null;
  skuShortDescription: string | null;
  sellerOrgId: string;
  buyerOrgId: string | null;
  salePriceUsd: string;
  feesUsd: string | null;
  netToSellerUsd: string | null;
  conditionBand: string | null;
  currencyCode: string | null;
  lifecycleState?: string | null;
}

interface ApiError extends Error {
  status?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TransactionDetailPage() {
  const { transactionId } = Route.useParams();
  const { orgType, user } = useAuth();
  const queryClient = useQueryClient();

  const txQ = useQuery<TransactionDetail, ApiError>({
    queryKey: qk.transactions.detail(transactionId),
    // NOTE (SIL-326 port): apps/api's clientTransactions router only exposes
    // GET / (list) and PATCH /:id/approve — there is no GET /:id. This call
    // pre-dates the port and was already hitting a route that doesn't exist
    // server-side (a genuine drift, not introduced here); kept on the plain
    // authFetch pipeline rather than the typed client since hc has no route
    // to represent. Flagged for a follow-up fix (see SIL-326 PR notes).
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/api/client/transactions/${transactionId}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        throw Object.assign(new Error(body.error?.message ?? `HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as TransactionDetail;
    },
    retry: (failCount, err) => err.status !== 404 && failCount < 2,
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.transactions[':id'].approve.$patch({
        param: { id: transactionId },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.transactions.detail(transactionId) });
      void queryClient.invalidateQueries({ queryKey: qk.transactions.all });
    },
  });

  return (
    <div className="space-y-6">
      <BackLink as={Link} to="/transactions">
        Transactions
      </BackLink>

      <QueryBoundary query={txQ} errorTitle="Couldn't load transaction details.">
        {(tx) => {
          // Approve button visible only for seller admins on pending transactions
          const canApprove =
            orgType === 'seller' &&
            user?.role === 'admin' &&
            tx.lifecycleState === 'pending_seller_approval';

          return (
            <>
              {/* Header */}
              <div className="flex items-start justify-between">
                <PageHeader
                  title="Transaction detail"
                  subtitle={`${formatDate(tx.closedAt)} · ${tx.id.slice(0, 8)}…`}
                />

                {canApprove && (
                  <Button className="disabled:opacity-60 gap-2 inline-flex items-center shadow-sm"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate()}
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <CheckCircle className="size-4" />
                    )}
                    Approve
                  </Button>
                )}

                {approveMutation.isSuccess && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle className="size-4" />
                    Approved
                  </div>
                )}
              </div>

              {approveMutation.isError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  Failed to approve: {(approveMutation.error as Error).message}
                </div>
              )}

              {/* Detail card */}
              <Card>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      SKU
                    </dt>
                    <dd className="mt-1 text-sm text-foreground">
                      {tx.skuShortDescription ?? tx.skuId ?? '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Condition
                    </dt>
                    <dd className="mt-1">
                      <ConditionBadge value={tx.conditionBand} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Status
                    </dt>
                    <dd className="mt-1 text-sm text-foreground">{tx.lifecycleState ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Sale price
                    </dt>
                    <dd className="mt-1">
                      <UsdCell value={Number(tx.salePriceUsd)} className="text-base" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Fees
                    </dt>
                    <dd className="mt-1">
                      <UsdCell value={tx.feesUsd != null ? Number(tx.feesUsd) : null} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Net to seller
                    </dt>
                    <dd className="mt-1">
                      <UsdCell value={tx.netToSellerUsd != null ? Number(tx.netToSellerUsd) : null} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Currency
                    </dt>
                    <dd className="mt-1 text-sm text-foreground">{tx.currencyCode ?? '—'}</dd>
                  </div>
                  {tx.assetId && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Asset
                      </dt>
                      <dd className="mt-1">
                        <Link
                          to="/assets/$assetId"
                          params={{ assetId: tx.assetId }}
                          className="text-sm text-brand-600 hover:underline"
                        >
                          {tx.assetId.slice(0, 8)}…
                        </Link>
                      </dd>
                    </div>
                  )}
                </dl>
              </Card>
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}
